import { createContext, useEffect, useRef, useState } from "react"
import { adminGetPostsV2, auth, logout } from "../util/firebase"
import { useAuthState } from "react-firebase-hooks/auth"
import { useNavigate, useSearchParams } from "react-router-dom"

import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
import MuiDrawer from "@mui/material/Drawer"
import Box from "@mui/material/Box"
import MuiAppBar from "@mui/material/AppBar"
import Toolbar from "@mui/material/Toolbar"
import List from "@mui/material/List"
import Typography from "@mui/material/Typography"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import Container from "@mui/material/Container"
import MenuIcon from "@mui/icons-material/Menu"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import useMediaQuery from "@mui/material/useMediaQuery"
import LogoutIcon from "@mui/icons-material/Logout"
import QuizIcon from "@mui/icons-material/Quiz"
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed"
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService"
import DashboardIcon from "@mui/icons-material/Dashboard"
import DescriptionIcon from "@mui/icons-material/Description"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import ListSubheader from "@mui/material/ListSubheader"
import PeopleIcon from "@mui/icons-material/People"
import MenuBookIcon from "@mui/icons-material/MenuBook"

import logo from "../assets/logo.svg"
import Posts from "../components/Posts"
import ConfirmationDialog from "../components/ConfirmationDialog"
import BootstrapSnackBar from "../components/BootstrapSnackBar"
import PriceSearchDialog from "../components/PriceSearchDialog"
import UserProfileDialog from "../components/UserProfileDialog"
import PostActionsDialog from "../components/PostActionsDialog"
import Questions from "../components/Questions"
import Users from "../components/Users"
import Tools from "../components/Tools"
import EditGovIlData from "../components/EditGovIlData"
import Dashboard from "../components/Dashboard"
import EditGallery from "../components/EditGallery"
import FakeSaleConfirmationDialog from "../components/FakeSaleConfirmationDialog"
import Descriptions from "../components/Descriptions"
import Dictionaries from "../components/Dictionaries"
import NotificationTemplates from "../components/NotificationTemplates"
import MarkChatUnreadIcon from "@mui/icons-material/MarkChatUnread"

const MainListItems = () => {
	const navigate = useNavigate()

	return (
		<>
			<ListItemButton onClick={() => navigate("/?tab=posts")}>
				<ListItemIcon>
					<DynamicFeedIcon color="white" />
				</ListItemIcon>
				<ListItemText primary="מודעות" primaryTypographyProps={{ style: { color: "white" } }} />
			</ListItemButton>
			<ListItemButton onClick={() => navigate("/?tab=questions")}>
				<ListItemIcon>
					<QuizIcon color="white" />
				</ListItemIcon>
				<ListItemText primary="שאלות" primaryTypographyProps={{ style: { color: "white" } }} />
			</ListItemButton>
			<ListItemButton onClick={() => navigate("/?tab=users")}>
				<ListItemIcon>
					<PeopleIcon color="white" />
				</ListItemIcon>
				<ListItemText primary="משתמשים" primaryTypographyProps={{ style: { color: "white" } }} />
			</ListItemButton>
			<ListItemButton onClick={() => navigate("/?tab=descriptions")}>
				<ListItemIcon>
					<DescriptionIcon color="white" />
				</ListItemIcon>
				<ListItemText primary="תאורי רכבים" primaryTypographyProps={{ style: { color: "white" } }} />
			</ListItemButton>
			<ListItemButton onClick={() => navigate("/?tab=tools")}>
				<ListItemIcon>
					<HomeRepairServiceIcon color="white" />
				</ListItemIcon>
				<ListItemText primary="כלים" primaryTypographyProps={{ style: { color: "white" } }} />
			</ListItemButton>
			<ListItemButton onClick={() => navigate("/?tab=dashboard")}>
				<ListItemIcon>
					<DashboardIcon color="white" />
				</ListItemIcon>
				<ListItemText primary="דשבורד (beta)" primaryTypographyProps={{ style: { color: "white" } }} />
			</ListItemButton>
			<ListItemButton onClick={() => navigate("/?tab=dictionaries")}>
				<ListItemIcon>
					<MenuBookIcon color="white" />
				</ListItemIcon>
				<ListItemText primary="תוכן" primaryTypographyProps={{ style: { color: "white" } }} />
			</ListItemButton>
			<ListItemButton onClick={() => navigate("/?tab=notificationTemplates")}>
				<ListItemIcon>
					<MarkChatUnreadIcon color="white" />
				</ListItemIcon>
				<ListItemText primary="נוטיפיקציות" primaryTypographyProps={{ style: { color: "white" } }} />
			</ListItemButton>
		</>
	)
}

const SecondaryListItems = ({ logout }) => {
	return (
		<>
			<ListSubheader component="div" inset sx={{ backgroundColor: "#132242", color: "#FFFFFF" }}>
				פעולות
			</ListSubheader>
			<ListItemButton onClick={() => logout()}>
				<ListItemIcon>
					<LogoutIcon color="white" />
				</ListItemIcon>
				<ListItemText primary="יציאה" primaryTypographyProps={{ style: { color: "#FFFFFF" } }} />
			</ListItemButton>
		</>
	)
}

const drawerWidth = 240

const mdTheme = createTheme({
	palette: {
		primary: {
			main: "#132242",
		},
		secondary: {
			main: "#DF0A0A",
		},
		white: {
			main: "white",
		},
	},
})

export const AdminContext = createContext(null)

const Admin = () => {
	const [user, loading] = useAuthState(auth)
	const matches = useMediaQuery(mdTheme.breakpoints.up("md"))
	const [open, setOpen] = useState(matches)

	const [searchParams] = useSearchParams()
	const tab = searchParams.get("tab") ?? "dashboard"
	const navigate = useNavigate()
	//const [contentPane, setContentPane] = useState(4)

	const containerRef = useRef(null)
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
	const [fakeSaleConfirmationDialog, setFakeSaleConfirmationDialog] = useState(false)
	const [fakeBidDialog, setFakeBidDialog] = useState(false)
	const [fakeSaleUserId, setFakeSaleUserId] = useState("")
	const [fakeSalePrice, setFakeSalePrice] = useState("")
	const [confirmationMessage, setConfirmationMessage] = useState(null)
	const [confirmationCallback, setConfirmationCallback] = useState(() => {})
	const [snackbarMessage, setSnackbarMessage] = useState(null)
	const [snackbarOpen, setSnackbarOpen] = useState(false)
	const [snackbarSeverity, setSnackbarSeverity] = useState(null)
	const [priceSearchOpen, setPriceSearchOpen] = useState(false)
	const [editGovIlDataOpen, setEditGovIlDataOpen] = useState(false)
	const [editGalleryOpen, setEditGalleryOpen] = useState(false)
	const [userDialogOpen, setUserDialogOpen] = useState(false)
	const [userId, setUserId] = useState(null)

	const [postOptionsOpen, setPostOptionsOpen] = useState(false)
	const [post, setPost] = useState(null)

	const [postPriceScoreMin, setPostPriceScoreMin] = useState("")
	const [postPriceScoreMax, setPostPriceScoreMax] = useState("")
	const [postPriceScoreValues, setPostPriceScoreValues] = useState([])

	const [bloggerDescription, setBloggerDescription] = useState("")

	const handlePostOptionOpen = async (postId, status) => {
		const response = await adminGetPostsV2({ status: status, id: postId })
		if (response.data.status === 0 && response.data.posts.length > 0) {
			setPost(response.data.posts[0])
			setPostOptionsOpen(true)
		}

		/* setPost(posts[index])
		setPostOptionsOpen(true) */
	}

	const handlePostOptionsClose = (event, reason) => {
		if (reason !== "backdropClick") {
			setPost(null)
			setPostPriceScoreMin("")
			setPostPriceScoreMax("")
			setPostPriceScoreValues([])
			setPostOptionsOpen(false)
			setBloggerDescription("")
		}
	}

	const refreshPost = async (postId, status) => {
		const response = await adminGetPostsV2({ status: status, id: postId })
		if (response.data.status === 0 && response.data.posts.length > 0) {
			setPost(response.data.posts[0])
		}
	}

	const renderStep = (state) => {
		switch (state.toString()) {
			case "posts":
				return <Posts containerRef={containerRef} />
			case "questions":
				return <Questions containerRef={containerRef} />
			case "users":
				return <Users />
			case "tools":
				return <Tools />
			case "dashboard":
				return <Dashboard />
			case "descriptions":
				return <Descriptions />
			case "dictionaries":
				return <Dictionaries />
			case "notificationTemplates":
				return <NotificationTemplates />
			default:
				break
		}
	}

	useEffect(() => {
		if (!matches) {
			setOpen(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [matches])

	useEffect(() => {
		if (loading) {
			// maybe trigger a loading screen
			return
		}
		const getToken = async () => {
			const idTokenResult = await user.getIdTokenResult()
			if (!idTokenResult.claims.admin) {
				logout().then(() => {
					navigate("/login")
				})
			}
		}
		if (user?.emailVerified) {
			getToken()
		} else if (user) {
			logout().then(() => {
				navigate("/login")
			})
		} else {
			navigate("/login")
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, user])

	const toggleDrawer = () => {
		setOpen(!open)
	}

	const handleConfirmationDialogClose = () => {
		setConfirmationDialogOpen(false)
	}

	const handleFakeSaleConfirmationDialogClose = () => {
		setFakeSaleConfirmationDialog(false)
	}

	const handleFakeSaleConfirmationDialogOpen = (message, highlight, okCallback) => {
		console.log(okCallback)
		setConfirmationMessage({ message: message, highlight: highlight })
		setConfirmationCallback(() => okCallback)
		setFakeSaleConfirmationDialog(true)
	}

	useEffect(() => {
		console.log(fakeSalePrice + "!!!")
	}, [fakeSalePrice])

	const handleConfirmationDialogOpen = (message, highlight, okCallback) => {
		console.log(okCallback)
		setConfirmationMessage({ message: message, highlight: highlight })
		setConfirmationCallback(() => okCallback)
		setConfirmationDialogOpen(true)
	}

	const handleOpenSnackbar = (message, severity) => {
		setSnackbarMessage(message)
		setSnackbarSeverity(severity)
		setSnackbarOpen(true)
	}

	const handleUserDialogOpen = (userId) => {
		setUserId(userId)
		setUserDialogOpen(true)
	}

	const contextObject = {
		confirmationDialogOpen: confirmationDialogOpen,
		setConfirmationDialogOpen: setConfirmationDialogOpen,
		handleConfirmationDialogClose: handleConfirmationDialogClose,
		handleConfirmationDialogOpen: handleConfirmationDialogOpen,
		setSnackbarMessage: setSnackbarMessage,
		setSnackbarOpen: setSnackbarOpen,
		snackbarMessage: snackbarMessage,
		snackbarOpen: snackbarOpen,
		handleOpenSnackbar: handleOpenSnackbar,
		snackbarSeverity: snackbarSeverity,
		setSnackbarSeverity: setSnackbarSeverity,
		priceSearchOpen: priceSearchOpen,
		setPriceSearchOpen: setPriceSearchOpen,
		handleUserDialogOpen: handleUserDialogOpen,
		userDialogOpen: userDialogOpen,
		setUserDialogOpen: setUserDialogOpen,
		userId: userId,
		setUserId: setUserId,
		post: post,
		setPost: setPost,
		postOptionsOpen: postOptionsOpen,
		setPostOptionsOpen: setPostOptionsOpen,
		handlePostOptionOpen: handlePostOptionOpen,
		handlePostOptionsClose: handlePostOptionsClose,
		refreshPost: refreshPost,
		postPriceScoreMax: postPriceScoreMax,
		setPostPriceScoreMax: setPostPriceScoreMax,
		postPriceScoreMin: postPriceScoreMin,
		setPostPriceScoreMin: setPostPriceScoreMin,
		postPriceScoreValues: postPriceScoreValues,
		setPostPriceScoreValues: setPostPriceScoreValues,
		editGovIlDataOpen: editGovIlDataOpen,
		setEditGovIlDataOpen: setEditGovIlDataOpen,
		editGalleryOpen: editGalleryOpen,
		setEditGalleryOpen: setEditGalleryOpen,
		bloggerDescription: bloggerDescription,
		setBloggerDescription: setBloggerDescription,
		fakeSaleConfirmationDialog: fakeSaleConfirmationDialog,
		setFakeSaleConfirmationDialog: setFakeSaleConfirmationDialog,
		handleFakeSaleConfirmationDialogClose: handleFakeSaleConfirmationDialogClose,
		handleFakeSaleConfirmationDialogOpen: handleFakeSaleConfirmationDialogOpen,
		fakeSaleUserId: fakeSaleUserId,
		setFakeSaleUserId: setFakeSaleUserId,
		fakeSalePrice: fakeSalePrice,
		setFakeSalePrice: setFakeSalePrice,
		fakeBidDialog: fakeBidDialog,
		setFakeBidDialog: setFakeBidDialog,
	}
	return (
		<AdminContext.Provider value={contextObject}>
			<ThemeProvider theme={mdTheme}>
				<BootstrapSnackBar />
				{priceSearchOpen && <PriceSearchDialog />}
				<EditGovIlData />
				<EditGallery />
				<UserProfileDialog />
				{post && <PostActionsDialog />}
				<ConfirmationDialog message={confirmationMessage} okCallback={confirmationCallback} />
				<FakeSaleConfirmationDialog message={confirmationMessage} okCallback={confirmationCallback} />
				<Box sx={{ display: "flex" }}>
					<CssBaseline />
					<AppBar position="absolute" open={open} color="white">
						<Toolbar
							sx={{
								pr: "24px",
							}}
						>
							<IconButton
								edge="start"
								color="inherit"
								aria-label="open drawer"
								onClick={toggleDrawer}
								sx={{
									marginRight: "36px",
									...(open && { display: "none" }),
								}}
							>
								<MenuIcon />
							</IconButton>
							<img src={logo} style={{ marginLeft: "15px", width: "111px", height: "32px" }} alt="Logo" />
							<Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
								אדמין
							</Typography>
						</Toolbar>
					</AppBar>
					<Drawer
						variant="permanent"
						open={open}
						PaperProps={{
							sx: {
								backgroundColor: mdTheme.palette.primary.main,
							},
						}}
					>
						<Toolbar
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "flex-end",
								px: [1],
							}}
						>
							<IconButton onClick={toggleDrawer}>
								<ChevronRightIcon color="white" />
							</IconButton>
						</Toolbar>
						<Divider sx={{ background: "#FFFFFF" }} />
						<List component="nav">
							<MainListItems /* setContentPane={setContentPane} */ />
							<Divider sx={{ my: 1, background: "#FFFFFF" }} />
							<SecondaryListItems logout={logout} />
						</List>
					</Drawer>
					<Box
						component="main"
						sx={{
							backgroundColor: (theme) =>
								theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[900],
							flexGrow: 1,
							height: "100vh",
							overflow: "auto",
							scrollBehavior: "smooth",
						}}
						ref={containerRef}
					>
						<Toolbar />
						<Container maxWidth="false" sx={{ mt: 4, mb: 4 }}>
							{renderStep(tab)}
						</Container>
					</Box>
				</Box>
			</ThemeProvider>
		</AdminContext.Provider>
	)
}

const AppBar = styled(MuiAppBar, {
	shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
	zIndex: theme.zIndex.drawer + 1,
	transition: theme.transitions.create(["width", "margin"], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		marginLeft: drawerWidth,
		width: `calc(100% - ${drawerWidth}px)`,
		transition: theme.transitions.create(["width", "margin"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}))

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
	"& .MuiDrawer-paper": {
		position: "relative",
		whiteSpace: "nowrap",
		width: drawerWidth,
		transition: theme.transitions.create("width", {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
		boxSizing: "border-box",
		...(!open && {
			overflowX: "hidden",
			transition: theme.transitions.create("width", {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen,
			}),
			width: theme.spacing(7),
			[theme.breakpoints.up("sm")]: {
				width: theme.spacing(9),
			},
		}),
	},
}))

export default Admin
